import { BILLING_CYCLES, CHECKOUT_PRICING, PRODUCTS } from '../../../../../lib/billing';
import {
  buildFullCheckoutItems,
  computeFuturesAmountCents,
  computeLineAmountCents,
  computeTrialCapable,
  computeUseTrial,
  getExtraUnitAmountCents,
  getUnitAmountCents,
  PRICE_VARIANTS
} from '../../../../../lib/billing';

// Cost of `qty` copier accounts on `price`, honoring its tiers. Stripe bills a
// tiered price by the quantity as a whole (volume: every account at the tier the
// count lands in), so the total can't be derived from a single account's price —
// e.g. Standard annual at $120 for 1 / $100 each from 2 bills $200 for 2, not
// $240. Falls back to the static per-account rate when the catalog lacks it.
function copierLineCents(price, qty, fallbackPerAccountCents) {
  if (!(qty > 0)) return 0;
  return price ? computeLineAmountCents(price, qty) : fallbackPerAccountCents * qty;
}

// Per-account rate at the selected count — moves with the count on a tiered
// price. With nothing selected yet, quote a single account.
function copierPerAccountCents(price, qty, fallbackPerAccountCents) {
  const n = qty > 0 ? qty : 1;
  return Math.round(copierLineCents(price, n, fallbackPerAccountCents) / n);
}

export function paidMonthsFor(billingCycle) {
  const mul = CHECKOUT_PRICING.cycleMultipliers[billingCycle] ?? 1;
  const free = CHECKOUT_PRICING.cycleMonthsFree[billingCycle] ?? 0;
  return Math.max(0, mul - free);
}

export function getCopierStandardCycleCents(billingCycle) {
  return CHECKOUT_PRICING.copier.standardPerAccountCents * paidMonthsFor(billingCycle);
}

export function getCopierPremiumCycleCents(billingCycle) {
  return CHECKOUT_PRICING.copier.premiumPerAccountCents * paidMonthsFor(billingCycle);
}

// Landing-only Futures display pricing. Real checkout values come from /prices.
export function getFuturesCycleCents(qty, billingCycle) {
  if (qty <= 0) return 0;
  const { firstAccountMonthlyCents, extraAccountMonthlyCents } = CHECKOUT_PRICING.futures;
  const months = paidMonthsFor(billingCycle);
  const extras = Math.max(0, qty - 1);
  return (firstAccountMonthlyCents + extras * extraAccountMonthlyCents) * months;
}

export function getFuturesMonthlyCents(qty) {
  if (qty <= 0) return 0;
  const { firstAccountMonthlyCents, extraAccountMonthlyCents } = CHECKOUT_PRICING.futures;
  return firstAccountMonthlyCents + Math.max(0, qty - 1) * extraAccountMonthlyCents;
}

export function computePricing(selection, catalog = null, trialUsedProductIds = []) {
  const { billingCycle, standardCount, premiumCount, futuresCount, futuresUnlimited } = selection;
  const { copier, cycleMultipliers, cycleMonthsFree } = CHECKOUT_PRICING;

  const multiplier = cycleMultipliers[billingCycle] ?? 1;
  const monthsFree = cycleMonthsFree[billingCycle] ?? 0;
  const paidMonths = Math.max(0, multiplier - monthsFree);

  const wantsStandard = standardCount > 0;
  const wantsPremium = premiumCount > 0;
  // The unlimited toggle only takes effect when the catalog actually stocks an
  // unlimited Futures SKU for the selected cycle. If the user requested
  // unlimited but it isn't available (e.g. after switching to a cycle that
  // lacks the flat SKU), fall back to the tiered variant so the card and price
  // never resolve to a missing/$0 SKU.
  const hasUnlimitedFutures = Boolean(catalog?.has?.(PRODUCTS.futures, billingCycle, PRICE_VARIANTS.unlimited));
  const effectiveFuturesUnlimited = Boolean(futuresUnlimited) && hasUnlimitedFutures;
  const wantsFutures = effectiveFuturesUnlimited || futuresCount > 0;
  const futuresVariant = effectiveFuturesUnlimited ? PRICE_VARIANTS.unlimited : PRICE_VARIANTS.default;
  // Effective quantity sent to per-line pricing math. Unlimited is a flat SKU
  // billed as qty=1; tiered uses the user-selected account count.
  const futuresEffectiveQty = effectiveFuturesUnlimited ? 1 : futuresCount;

  // Catalog prices are cycle-totals (e.g. annual price = year cost), not per-month.
  const stdPrice = catalog?.get(PRODUCTS.standard, billingCycle) || null;
  const premPrice = catalog?.get(PRODUCTS.premium, billingCycle) || null;
  const monthlyStd = catalog?.get(PRODUCTS.standard, BILLING_CYCLES.monthly) || null;
  const monthlyPrem = catalog?.get(PRODUCTS.premium, BILLING_CYCLES.monthly) || null;
  const futPrice = catalog?.get(PRODUCTS.futures, billingCycle, futuresVariant) || null;
  const monthlyFut = catalog?.get(PRODUCTS.futures, BILLING_CYCLES.monthly, futuresVariant) || null;

  // Per-account monthly rate at the selected count.
  const stdPerMonthCents = copierPerAccountCents(monthlyStd, standardCount, copier.standardPerAccountCents);
  const premPerMonthCents = copierPerAccountCents(monthlyPrem, premiumCount, copier.premiumPerAccountCents);

  // Per-account price for the selected cycle at the selected count. Flat per_unit
  // prices give the same rate at any count; tiered ones (Standard annual) drop
  // once the count crosses a tier.
  const stdUnitCycleCents = copierPerAccountCents(stdPrice, standardCount, copier.standardPerAccountCents * paidMonths);
  const premUnitCycleCents = copierPerAccountCents(premPrice, premiumCount, copier.premiumPerAccountCents * paidMonths);

  // Futures is graduated: a flat first-account price + a per-extra-account rate.
  // Use the price row for the SELECTED cycle so the display note tracks the
  // billing cycle (e.g. annual shows the yearly flat + yearly per-extra rate).
  const futuresFirstCycleCents = futPrice ? getUnitAmountCents(futPrice) : getFuturesCycleCents(1, billingCycle);
  const futuresExtraCycleCents = futPrice
    ? getExtraUnitAmountCents(futPrice)
    : getFuturesCycleCents(2, billingCycle) - getFuturesCycleCents(1, billingCycle);

  // Monthly subtotals — also the "would be" baseline the cycle savings compare against.
  const standardCents = copierLineCents(monthlyStd, standardCount, copier.standardPerAccountCents);
  const premiumCents = copierLineCents(monthlyPrem, premiumCount, copier.premiumPerAccountCents);
  const futuresMonthlyCents = wantsFutures
    ? monthlyFut
      ? computeFuturesAmountCents(monthlyFut, futuresEffectiveQty)
      : getFuturesMonthlyCents(futuresEffectiveQty)
    : 0;
  const monthlyCents = standardCents + premiumCents + futuresMonthlyCents;

  // Cycle (billed-now) line totals — all catalog-driven and tier-aware when a catalog exists.
  const standardCycleCents = copierLineCents(stdPrice, standardCount, copier.standardPerAccountCents * paidMonths);
  const premiumCycleCents = copierLineCents(premPrice, premiumCount, copier.premiumPerAccountCents * paidMonths);
  const futuresCycleCents = wantsFutures
    ? futPrice
      ? computeFuturesAmountCents(futPrice, futuresEffectiveQty)
      : getFuturesCycleCents(futuresEffectiveQty, billingCycle)
    : 0;
  const cycleCents = standardCycleCents + premiumCycleCents + futuresCycleCents;

  // Display total account count — unlimited variant doesn't have a "count" in
  // the per-account sense; show 0 here and let the UI render an "Unlimited"
  // chip on the futures card when futuresUnlimited is true.
  const totalAccounts = standardCount + premiumCount + (effectiveFuturesUnlimited ? 0 : futuresCount);
  const discount = multiplier > 0 ? monthsFree / multiplier : 0;

  // Cart-wide trial eligibility = every requested price has the SAME
  // trial_period_days > 0. Mirrors PaymentStep / preview sync via the shared
  // `computeUseTrial` helper so the UI badge, the preview total and the real
  // checkout request stay in lockstep. Backend re-validates per-price (tiered
  // scheme, qty within included bundle, history) before actually granting.
  const trialItems = catalog ? buildFullCheckoutItems(selection, catalog) : [];
  // Three layers:
  //  • trialCapable  — cart is a trial-enabled tiered product (ignores qty). Drives
  //    whether the trial choice is *shown* at all.
  //  • trialEligible — capable AND within the included bundle (qty ≤ base). Drives
  //    whether the "Free trial" option is *selectable* (greyed when over the base).
  //  • trialApplies  — eligible AND the buyer didn't pick "Buy now" (skipTrial).
  // `trialDays`/`trialIncludedBase` come from capability so the choice can render
  // its label + "covers up to N accounts" note even when over the limit.
  // Already-used products (per-product one-time trial) drop out of capability,
  // so the trial choice disappears on a second billing cycle / repeat purchase.
  const trialCapable = Boolean(catalog) && trialItems.length > 0 && computeTrialCapable(trialItems, catalog, trialUsedProductIds);
  const trialEligible = trialCapable && computeUseTrial(trialItems, catalog, trialUsedProductIds);
  const trialApplies = trialEligible && !selection.skipTrial;

  const trialDays = trialCapable ? Number(catalog?.getById?.(trialItems[0]?.price_id)?.trial_period_days ?? 0) : 0;
  const trialIncludedBase = (() => {
    const upTo = futPrice?.tiers?.[0]?.up_to ?? futPrice?.tiers?.[0]?.upTo;
    const n = Number(upTo);
    return Number.isFinite(n) && n > 1 ? n : CHECKOUT_PRICING.futures.tieredIncludedAccounts ?? 20;
  })();

  return {
    standardCents,
    premiumCents,
    futuresMonthlyCents,
    standardCycleCents,
    premiumCycleCents,
    futuresCycleCents,
    monthlyCents,
    cycleCents,
    stdPerMonthCents,
    premPerMonthCents,
    stdUnitCycleCents,
    premUnitCycleCents,
    futuresFirstCycleCents,
    futuresExtraCycleCents,
    futuresUnlimited: !!futuresUnlimited,
    effectiveFuturesUnlimited,
    multiplier,
    monthsFree,
    paidMonths,
    discount,
    totalAccounts,
    trialDays,
    trialCapable,
    trialEligible,
    trialApplies,
    trialIncludedBase,
    skipTrial: !!selection.skipTrial,
    wantsStandard,
    wantsPremium,
    wantsFutures
  };
}
